import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import { supabase } from 'lib/supabase';


const ProBonoAssignment = ({ compact = false }) => {
  const [cases, setCases] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCase, setSelectedCase] = useState(null);
  const [assigningLawyer, setAssigningLawyer] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch pro bono cases
      const { data: casesData, error: casesError } = await supabase
        ?.from('pro_bono_cases')
        ?.select(`
          *,
          client:client_id (
            full_name,
            email,
            phone
          ),
          lawyer:assigned_lawyer_id (
            full_name,
            email
          )
        `)
        ?.order('created_at', { ascending: false });

      if (casesError) throw casesError;

      // Fetch available lawyers
      const { data: lawyersData, error: lawyersError } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, email')
        ?.eq('role', 'lawyer')
        ?.eq('status', 'active');

      if (lawyersError) throw lawyersError;

      setCases(casesData || []);
      setLawyers(lawyersData || []);
    } catch (error) {
      console.error('Error fetching pro bono data:', error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignLawyer = async (caseId, lawyerId) => {
    try {
      const currentUser = await supabase?.auth?.getUser();

      const { error } = await supabase
        ?.from('pro_bono_cases')
        ?.update({
          assigned_lawyer_id: lawyerId,
          assignment_status: 'assigned',
          assigned_at: new Date()?.toISOString(),
          assigned_by: currentUser?.data?.user?.id
        })
        ?.eq('id', caseId);

      if (error) throw error;

      // Log action
      await supabase?.rpc('log_user_action', {
        p_user_id: currentUser?.data?.user?.id,
        p_action: 'pro_bono_case_assigned',
        p_resource_type: 'pro_bono_case',
        p_resource_id: caseId,
        p_details: JSON.stringify({ lawyer_id: lawyerId }),
        p_severity: 'info'
      });

      fetchData();
      setSelectedCase(null);
      setAssigningLawyer(null);
    } catch (error) {
      console.error('Error assigning lawyer:', error?.message);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      unassigned: { bg: 'bg-warning-100', text: 'text-warning-700', label: 'Unassigned', icon: 'AlertCircle' },
      assigned: { bg: 'bg-secondary-100', text: 'text-secondary-700', label: 'Assigned', icon: 'UserCheck' },
      in_progress: { bg: 'bg-primary-100', text: 'text-primary-700', label: 'In Progress', icon: 'Clock' },
      completed: { bg: 'bg-success-100', text: 'text-success-700', label: 'Completed', icon: 'CheckCircle' },
      closed: { bg: 'bg-secondary-100', text: 'text-secondary-700', label: 'Closed', icon: 'XCircle' }
    };
    const config = statusConfig?.[status] || statusConfig?.unassigned;
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
        <Icon name={config?.icon} size={12} />
        <span>{config?.label}</span>
      </span>
    );
  };

  const getUrgencyBadge = (urgency) => {
    const urgencyConfig = {
      low: { bg: 'bg-secondary-100', text: 'text-secondary-700', label: 'Low' },
      medium: { bg: 'bg-primary-100', text: 'text-primary-700', label: 'Medium' },
      high: { bg: 'bg-warning-100', text: 'text-warning-700', label: 'High' },
      critical: { bg: 'bg-error-100', text: 'text-error-700', label: 'Critical' }
    };
    const config = urgencyConfig?.[urgency] || urgencyConfig?.medium;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
        {config?.label}
      </span>
    );
  };

  const unassignedCases = cases?.filter(c => c?.assignment_status === 'unassigned');
  const displayCases = compact ? unassignedCases?.slice(0, 3) : cases;

  if (loading) {
    return (
      <div className="bg-surface rounded-lg shadow-card p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-secondary-100 rounded w-1/3"></div>
          <div className="h-32 bg-secondary-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg shadow-card">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-heading font-bold text-text-primary">Pro Bono Case Assignment</h2>
            <p className="text-sm text-text-secondary mt-1">
              {unassignedCases?.length} cases awaiting lawyer assignment
            </p>
          </div>
          {compact && unassignedCases?.length > 3 && (
            <button className="text-primary text-sm font-medium hover:underline">
              View All
            </button>
          )}
        </div>
      </div>

      <div className="divide-y divide-border">
        {displayCases?.length === 0 ? (
          <div className="p-12 text-center">
            <Icon name="Heart" size={48} className="mx-auto text-text-secondary mb-4" />
            <p className="text-text-secondary">No pro bono cases</p>
          </div>
        ) : (
          displayCases?.map((probonoCase) => (
            <div key={probonoCase?.id} className="p-6 hover:bg-secondary-50 transition-smooth">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="font-medium text-text-primary">{probonoCase?.case_title}</h3>
                    {getUrgencyBadge(probonoCase?.urgency_level)}
                  </div>
                  <p className="text-sm text-text-secondary mb-3">{probonoCase?.case_description}</p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-sm">
                      <Icon name="User" size={14} className="text-text-secondary" />
                      <span className="text-text-secondary">Client: </span>
                      <span className="text-text-primary font-medium">{probonoCase?.client?.full_name}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <Icon name="Briefcase" size={14} className="text-text-secondary" />
                      <span className="text-text-secondary">Practice Area: </span>
                      <span className="text-text-primary">{probonoCase?.practice_area}</span>
                    </div>
                    {probonoCase?.lawyer && (
                      <div className="flex items-center space-x-2 text-sm">
                        <Icon name="UserCheck" size={14} className="text-text-secondary" />
                        <span className="text-text-secondary">Assigned to: </span>
                        <span className="text-text-primary font-medium">{probonoCase?.lawyer?.full_name}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end space-y-2">
                  {getStatusBadge(probonoCase?.assignment_status)}
                  {probonoCase?.assignment_status === 'unassigned' && (
                    <div className="mt-2">
                      {selectedCase === probonoCase?.id ? (
                        <div className="space-y-2">
                          <select
                            value={assigningLawyer || ''}
                            onChange={(e) => setAssigningLawyer(e?.target?.value)}
                            className="px-3 py-1 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                          >
                            <option value="">Select Lawyer</option>
                            {lawyers?.map((lawyer) => (
                              <option key={lawyer?.id} value={lawyer?.id}>
                                {lawyer?.full_name}
                              </option>
                            ))}
                          </select>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleAssignLawyer(probonoCase?.id, assigningLawyer)}
                              disabled={!assigningLawyer}
                              className="flex items-center space-x-1 px-3 py-1 bg-primary text-white rounded-lg text-sm hover:bg-primary-700 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Icon name="Check" size={14} />
                              <span>Assign</span>
                            </button>
                            <button
                              onClick={() => {
                                setSelectedCase(null);
                                setAssigningLawyer(null);
                              }}
                              className="flex items-center space-x-1 px-3 py-1 bg-secondary-100 text-text-primary rounded-lg text-sm hover:bg-secondary-200 transition-smooth"
                            >
                              <Icon name="X" size={14} />
                              <span>Cancel</span>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setSelectedCase(probonoCase?.id)}
                          className="flex items-center space-x-1 px-3 py-1 bg-primary text-white rounded-lg text-sm hover:bg-primary-700 transition-smooth"
                        >
                          <Icon name="UserPlus" size={14} />
                          <span>Assign Lawyer</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ProBonoAssignment;